import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import cn from "classnames";
import styles from "./Messages.module.sass";
import MessagingCenter from "../../components/Messaging/MessagingCenter";
import { useChatContext } from "../../contexts/ChatContext";
import streamChatService from "../../services/streamChat";
import BlurredGate from "../../components/BlurredGate";

const Messages = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { initializeChat, isConnected, startConversation, setActiveChannel } = useChatContext();
    
    // Get user info from localStorage
    const email = typeof window !== 'undefined' ? 
        window.localStorage.getItem('postaway_email') || '' : '';
    const userType = typeof window !== 'undefined' ? 
        window.localStorage.getItem('postaway_user_type') || 'host' : 'host';
    
    // Get the actual user ID from the auth token or user data
    const authToken = typeof window !== 'undefined' ?
        window.localStorage.getItem('postaway_auth_token') : null;
    
    // Use the Stream service's current user ID if connected, otherwise null
    // This ensures we use the exact same ID that Stream is using
    const [currentUserId, setCurrentUserId] = useState(null);
    const [membershipTier, setMembershipTier] = useState(null);
    
    useEffect(() => {
        // Update currentUserId when Stream connects
        if (isConnected && streamChatService.currentUserId) {
            setCurrentUserId(streamChatService.currentUserId);
        }
    }, [isConnected]);
    
    useEffect(() => {
        // Redirect if not logged in
        if (!email) {
            navigate(userType === 'creator' ? '/creator-login' : '/host-login');
            return;
        }
        
        // Basic members will see the BlurredGate instead of being redirected
        if (userType !== 'host' && userType !== 'creator') {
            // Travelers and partners see BlurredGate / can chat; non-users get redirected
            if (userType !== 'traveler' && userType !== 'partner') {
                navigate('/');
                return;
            }
        }

        // Check subscription status
        (async () => {
            try {
                const res = await fetch('/api/access/subscription', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email })
                });
                const data = await res.json();

                if (res.ok && data.ok && data.active) {
                    setMembershipTier(data.membershipTier || 'all_access');
                    if (data.membershipTier === 'basic') {
                        return;
                    }
                    // User has active subscription - initialize chat if needed
                    if (authToken && !isConnected) {
                        initializeChat(authToken).catch(console.error);
                    }
                } else {
                    // No active subscription - redirect to home
                    navigate('/');
                }
            } catch (err) {
                console.error('Subscription check error:', err);
                navigate('/');
            }
        })();
    }, [email, userType, navigate, initializeChat, isConnected, authToken]);

    // Collab-request context for the "Explore New Collab" flow (Collab
    // Tracker -> search -> "Send in Messages"). When present, the thread
    // shows a "Send Collab Request" banner so the sender can attach a formal
    // request to the DM.
    //
    // Note on the application gate: cold applications to collab posts go
    // through ApplyModal (no DM until the host approves), and nothing else in
    // the app links here with collabRequest=true — so honoring it doesn't
    // reopen the "creators land in my inbox uninvited" spam vector.
    const [collabRequestContext, setCollabRequestContext] = useState(null);

    // If ?to=<userId> is present, start/open the conversation. For
    // collabRequest=true links we also capture the collab context.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const to = params.get('to');
        const title = params.get('title'); // Optional title/name for the conversation
        const isCollabRequest = params.get('collabRequest') === 'true';
        const targetKind = params.get('targetKind');
        const targetId = params.get('targetId');
        let snapshot = null;
        try {
            const snapshotParam = params.get('snapshot');
            if (snapshotParam) snapshot = JSON.parse(snapshotParam);
        } catch (e) {
            console.error('Failed to parse snapshot:', e);
        }

        if (!to) return;

        if (isCollabRequest) {
            setCollabRequestContext({
                recipientUserId: to,
                targetKind,
                targetId,
                snapshot,
                title,
            });
        }

        const tryStart = async () => {
            try {
                // Pass the bare user ID and let the service handle user type detection.
                // Sanitize provided title to avoid exposing last names on initial render.
                let safeTitle = title;
                try {
                    const t = (title || '').trim();
                    if (t) {
                        const parts = t.split(/\s+/);
                        const looksLikeTwoWordHuman =
                            parts.length === 2 &&
                            /^[A-Za-z][A-Za-z'.-]*$/.test(parts[0]) &&
                            /^[A-Za-z][A-Za-z'.-]*$/.test(parts[1]);
                        safeTitle = looksLikeTwoWordHuman ? parts[0] : t;
                    }
                } catch (_) {}
                await startConversation(to, { name: safeTitle });
            } catch (error) {
                console.error('Failed to start conversation:', error);
            }
        };
        if (isConnected) {
            tryStart();
        } else if (authToken) {
            initializeChat(authToken).catch(console.error);
        }
    }, [isConnected, startConversation, initializeChat, authToken]);

    // Deep-link: if ?channelId=<id> is present, select that channel once connected.
    // Driven by location.search so it re-fires when the user clicks a different
    // notification while already on /messages.
    // After selecting, we use navigate(replace) to strip the param — this
    // properly updates React Router's location state so the effect won't
    // re-fire with the stale channelId and override manual thread selections.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const channelId = params.get('channelId');
        if (!channelId) return;

        const selectAndClean = () => {
            setActiveChannel(channelId);
            // Use React Router navigate (not window.history.replaceState)
            // so location.search is properly cleared in React's state.
            navigate('/messages', { replace: true });
        };

        if (isConnected) {
            selectAndClean();
        } else if (authToken) {
            initializeChat(authToken).catch(console.error);
        }
    }, [location.search, isConnected, setActiveChannel, initializeChat, authToken, navigate]);
    
    useEffect(() => {
        // Add keyboard shortcut for opening messages (Cmd/Ctrl + M)
        const handleKeyPress = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'm') {
                e.preventDefault();
                // Could open a specific modal or focus on input
            }
        };
        
        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, []);
    
    if (membershipTier === 'basic') {
        return (
            <BlurredGate userType={userType}>
                <div className={cn("section", styles.section)}>
                    <div className={styles.board}>
                        <div className={styles.content}>
                            <div style={{ minHeight: '70vh' }} />
                        </div>
                    </div>
                </div>
            </BlurredGate>
        );
    }
    
    return (
        <div className={cn("section", styles.section)}>
            <div className={styles.board}>
                <div className={styles.content}>
                    <MessagingCenter
                        currentUserId={currentUserId}
                        userType={userType}
                        noAirmail
                        collabRequestContext={collabRequestContext}
                        onCollabRequestSent={() => {
                            setCollabRequestContext(null);
                            navigate('/messages', { replace: true });
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default Messages;
